import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Users, DollarSign, Plane } from 'lucide-react';
import MemberCenterLayout from '../components/common/MemberCenterLayout';
import { supabase } from '../utils/supabaseClient';
import { UserContext } from '../App';
import { TRAVEL_BUDGET_LABELS } from '../utils/travelPostLabels';
import { useModal } from '../context/ModalContext';
import { createMemberRecordTabs } from '../utils/memberRecordTabs';

const TABS = createMemberRecordTabs('joined');

const APPLICATION_STATUS_BADGE = {
  pending: { text: '審核中', bg: '#fef9c3', color: '#ca8a04' },
  accepted: { text: '已加入', bg: '#dcfce7', color: '#16a34a' },
  rejected: { text: '未通過', bg: '#fee2e2', color: '#dc2626' },
};

const POST_STATUS_BADGE = {
  open: { text: '招募中', bg: '#eef4f9', color: '#4e8bca' },
  closed: { text: '已額滿', bg: '#f3f4f6', color: '#6b7280' },
  completed: { text: '已完成', bg: '#ede9fe', color: '#7c3aed' },
  cancelled: { text: '已取消', bg: '#fef2f2', color: '#b91c1c' },
};

function formatDateRange(start, end) {
  if (!start) return '日期未定';
  const fmt = (d) => {
    const dt = new Date(d);
    return `${dt.getMonth() + 1}/${dt.getDate()}`;
  };
  return end ? `${fmt(start)} - ${fmt(end)}` : fmt(start);
}

function formatBudget(budgetRange) {
  return TRAVEL_BUDGET_LABELS[budgetRange] || budgetRange;
}

function TravelPostCard({ post, badge, acceptedCount }) {
  const navigate = useNavigate();
  const statusBadge = POST_STATUS_BADGE[post.status] || POST_STATUS_BADGE.open;

  return (
    <button
      type="button"
      className="member-center-surface"
      onClick={() => navigate(`/travel/${post.id}`)}
      style={{
        width: '100%', backgroundColor: 'white', borderRadius: '20px', padding: '16px',
        boxShadow: '0 10px 28px rgba(15,23,42,0.055)', fontFamily: 'inherit', textAlign: 'left',
        cursor: 'pointer', border: '1px solid #dbe4ee', transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <MapPin size={14} color="#4e8bca" />
            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
              {post.destination}
              {post.country && <span style={{ fontWeight: '400', color: '#9ca3af', fontSize: '0.85rem' }}> · {post.country}</span>}
            </span>
          </div>
        </div>

        {/* 右側標籤 */}
        {badge === 'organized' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: '600', padding: '2px 8px', borderRadius: '12px', backgroundColor: statusBadge.bg, color: statusBadge.color, whiteSpace: 'nowrap' }}>
              {statusBadge.text}
            </span>
            <span style={{ fontSize: '0.7rem', fontWeight: '600', padding: '2px 8px', borderRadius: '12px', backgroundColor: '#ede9fe', color: '#7c3aed', whiteSpace: 'nowrap' }}>
              {acceptedCount} / {post.max_companions}人
            </span>
          </div>
        ) : (
          <span style={{ fontSize: '0.7rem', fontWeight: '600', padding: '2px 8px', borderRadius: '12px', backgroundColor: statusBadge.bg, color: statusBadge.color, whiteSpace: 'nowrap' }}>
            {statusBadge.text}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6b7280', fontSize: '0.8rem' }}>
          <Calendar size={13} />
          <span>{formatDateRange(post.trip_start_date, post.trip_end_date)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6b7280', fontSize: '0.8rem' }}>
          <Users size={13} />
          <span>找{post.max_companions}人</span>
        </div>
        {post.budget_range && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6b7280', fontSize: '0.8rem' }}>
            <DollarSign size={13} />
            <span>{formatBudget(post.budget_range)}</span>
          </div>
        )}
      </div>
    </button>
  );
}

function ApplicationCard({ application }) {
  const navigate = useNavigate();
  const post = application.travel_posts;
  if (!post) return null;
  const statusBadge = APPLICATION_STATUS_BADGE[application.status] || APPLICATION_STATUS_BADGE.pending;

  return (
    <button
      type="button"
      className="member-center-surface"
      onClick={() => navigate(`/travel/${post.id}`)}
      style={{
        width: '100%', backgroundColor: 'white', borderRadius: '20px', padding: '16px',
        boxShadow: '0 10px 28px rgba(15,23,42,0.055)', fontFamily: 'inherit', textAlign: 'left',
        cursor: 'pointer', border: '1px solid #dbe4ee', transition: 'transform 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <MapPin size={14} color="#4e8bca" />
          <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
            {post.destination}
            {post.country && <span style={{ fontWeight: '400', color: '#9ca3af', fontSize: '0.85rem' }}> · {post.country}</span>}
          </span>
        </div>
        <span style={{ fontSize: '0.7rem', fontWeight: '600', padding: '2px 8px', borderRadius: '12px', backgroundColor: statusBadge.bg, color: statusBadge.color, whiteSpace: 'nowrap' }}>
          {statusBadge.text}
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: application.message ? '10px' : '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6b7280', fontSize: '0.8rem' }}>
          <Calendar size={13} />
          <span>{formatDateRange(post.trip_start_date, post.trip_end_date)}</span>
        </div>
        {post.budget_range && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6b7280', fontSize: '0.8rem' }}>
            <DollarSign size={13} />
            <span>{formatBudget(post.budget_range)}</span>
          </div>
        )}
      </div>

      {application.message && (
        <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
          我的留言：「{application.message}」
        </div>
      )}
    </button>
  );
}

function EmptyState({ tab, onBrowse }) {
  const messages = {
    organized: { text: '還沒有發布揪旅伴', sub: '發起你的第一趟旅行吧！', showBtn: false },
    joined: { text: '還沒有加入任何旅行', sub: '去找找有沒有感興趣的旅伴吧', showBtn: true },
    pending: { text: '目前沒有待審核的申請', sub: '先去看看有沒有想加入的旅行', showBtn: true },
    history: { text: '還沒有旅行足跡紀錄', sub: '完成旅行後就會出現囉', showBtn: true },
  };
  const m = messages[tab];

  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <Plane size={42} color="#9cc2e6" strokeWidth={1.5} style={{ marginBottom: '12px' }} />
      <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>{m.text}</div>
      <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '24px' }}>{m.sub}</div>
      {m.showBtn && (
        <button
          onClick={onBrowse}
          style={{ padding: '10px 24px', backgroundColor: '#4e8bca', color: 'white', border: 'none', borderRadius: '20px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}
        >
          瀏覽旅伴
        </button>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="member-center-surface" style={{ backgroundColor: 'white', borderRadius: '20px', padding: '16px', boxShadow: '0 10px 28px rgba(15,23,42,0.055)' }}>
      <div style={{ height: '18px', width: '55%', backgroundColor: '#f1f5f9', borderRadius: '6px', marginBottom: '10px' }} />
      <div style={{ height: '13px', width: '70%', backgroundColor: '#f1f5f9', borderRadius: '6px' }} />
    </div>
  );
}

export default function MyTravelMates() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState(TABS.some(tab => tab.key === location.state?.tab) ? location.state.tab : 'organized');
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({ organized: [], joined: [], pending: [], history: [] });
  const { showAlert } = useModal();

  const loadMyTravel = useCallback(async () => {
    if (!currentUser?.id) return;
    setIsLoading(true);

    try {
      const today = new Date().toISOString().split('T')[0];

      // 我發起的貼文
      const { data: myPosts, error: e1 } = await supabase
        .from('travel_posts')
        .select(`
          id, destination, country, trip_start_date, trip_end_date,
          budget_range, max_companions, status,
          travel_applications ( status )
        `)
        .eq('organizer_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (e1) throw e1;

      // 我的申請（待審核 + 已加入）
      const { data: myApplications, error: e2 } = await supabase
        .from('travel_applications')
        .select(`
          id, message, status, created_at,
          travel_posts (
            id, destination, country, trip_start_date, trip_end_date,
            budget_range, max_companions, status
          )
        `)
        .eq('applicant_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (e2) throw e2;

      const organized = [];
      const history = [];
      (myPosts || []).forEach(post => {
        if (['completed', 'cancelled'].includes(post.status) || (post.trip_end_date && post.trip_end_date < today)) {
          history.push(post);
        } else {
          organized.push(post);
        }
      });

      const joined = [];
      const pending = [];
      (myApplications || []).forEach(app => {
        if (!app.travel_posts) return;
        const isCompleted = ['completed', 'cancelled'].includes(app.travel_posts.status) || (app.travel_posts.trip_end_date && app.travel_posts.trip_end_date < today);
        if (app.travel_posts.status === 'cancelled') {
          history.push({ ...app.travel_posts, _fromApplication: true });
        } else if (app.status === 'pending') {
          pending.push(app);
        } else if (app.status === 'accepted' && !isCompleted) {
          joined.push(app);
        } else if (app.status === 'accepted' && isCompleted) {
          history.push({ ...app.travel_posts, _fromApplication: true });
        }
      });

      setData({ organized, joined, pending, history });
    } catch (err) {
      console.error('loadMyTravel error:', err);
      showAlert('錯誤', '載入旅行資料失敗，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, showAlert]);

  useEffect(() => {
    loadMyTravel();
  }, [loadMyTravel]);

  const counts = {
    organized: data.organized.length,
    joined: data.joined.length,
    pending: data.pending.length,
    history: data.history.length,
  };

  return (
    <MemberCenterLayout title="我的旅行" subtitle="管理你的揪旅伴、申請與歷史紀錄" activeKey="travel">
        <div className="member-center-tabs" role="tablist" aria-label="我的旅行分類">
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            const count = counts[tab.key];
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.key)}
                className={`member-center-tab${isActive ? ' member-center-tab--active' : ''}`}
              >
                {tab.label}
                {count > 0 && <span className="member-center-tab__count">{count}</span>}
              </button>
            );
          })}
        </div>

        <div className="member-center-card-grid">
          {isLoading ? (
            <><SkeletonCard /><SkeletonCard /><SkeletonCard /></>
          ) : activeTab === 'organized' ? (
            data.organized.length === 0
              ? <EmptyState tab="organized" onBrowse={() => navigate('/travel')} />
              : data.organized.map(post => (
                <TravelPostCard
                  key={post.id}
                  post={post}
                  badge="organized"
                  acceptedCount={post.travel_applications?.filter(a => a.status === 'accepted').length ?? 0}
                />
              ))
          ) : activeTab === 'joined' ? (
            data.joined.length === 0
              ? <EmptyState tab="joined" onBrowse={() => navigate('/travel')} />
              : data.joined.map(app => <ApplicationCard key={app.id} application={app} />)
          ) : activeTab === 'pending' ? (
            data.pending.length === 0
              ? <EmptyState tab="pending" onBrowse={() => navigate('/travel')} />
              : data.pending.map(app => <ApplicationCard key={app.id} application={app} />)
          ) : activeTab === 'history' ? (
            data.history.length === 0
              ? <EmptyState tab="history" onBrowse={() => navigate('/travel')} />
              : data.history.map(item => (
                item._fromApplication
                  ? <TravelPostCard key={item.id + '_h'} post={item} badge="history" acceptedCount={0} />
                  : <TravelPostCard key={item.id} post={item} badge="organized" acceptedCount={item.travel_applications?.filter(a => a.status === 'accepted').length ?? 0} />
              ))
          ) : null}
        </div>
    </MemberCenterLayout>
  );
}
